import { costLogService } from './services';

// Track last saved cost to prevent duplicates and network flooding
const lastSavedCosts: Record<string, string> = {};

export const nonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  return value.trim().length > 0 ? value : null;
};

export const extractWhatToResponseText = (payload: any): string | null => {
  if (!payload) return null;
  return (
    nonEmptyString(payload.message) ||
    nonEmptyString(payload.Message) ||
    nonEmptyString(payload.text) ||
    nonEmptyString(payload.Text) ||
    nonEmptyString(payload.suggestion)
  );
};

export const hasRenderableWhatToResponse = (payload: any): boolean => {
  if (!payload) return false;
  return Boolean(
    extractWhatToResponseText(payload) ||
    nonEmptyString(payload.what_to_say) ||
    nonEmptyString(payload.WhatToSay) ||
    nonEmptyString(payload.how_to_say) ||
    nonEmptyString(payload.HowToSay)
  );
};

// Helper function to decode JWT token (duplicated from AuthContext to avoid circular deps or complex imports)
const decodeJWT = (token: string): any => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    return null
  }
}

// Helper function to get current center_id from token
export const getCurrentCenterId = (): string | null => {
  try {
    const raw = localStorage.getItem('backend_access_token') || ''
    const token = raw.replace(/^"+|"+$/g, '').trim()
    if (!token) return null;

    const decoded = decodeJWT(token);
    if (!decoded) return null;

    let centerId = decoded.center_id;
    // Handle "null" string or JSON encoded value
    if (centerId === 'null' || centerId === null) return null;

    if (typeof centerId === 'string' && centerId.startsWith('"') && centerId.endsWith('"')) {
      try { centerId = JSON.parse(centerId); } catch { }
    }
    return centerId || null;
  } catch (e) {
    return null;
  }
}

// Helper function to save cost data to Supabase
export const saveCostToSupabase = async (costData: any, agentName: string, callId?: string | null) => {
  try {
    if (!costData) {
      console.log(`[COST_LOG] No cost data provided for agent: ${agentName}`);
      return;
    }

    const costSignature = `${agentName}-${costData.timestamp}-${costData.total_cost}`;

    if (lastSavedCosts[agentName] === costSignature) {
      console.log(`[COST_LOG] Skipping duplicate cost save for ${agentName}: ${costSignature}`);
      return;
    }

    console.log(`[COST_LOG] Cost data for ${agentName}:`, costData);

    const inputCost = costData.input_cost !== null && costData.input_cost !== undefined ? costData.input_cost.toString() : '0';
    const outputCost = costData.output_cost !== null && costData.output_cost !== undefined ? costData.output_cost.toString() : '0';
    const totalCost = costData.total_cost !== null && costData.total_cost !== undefined ? costData.total_cost.toString() : '0';
    const inputTokens = costData.input_tokens || 0;
    const outputTokens = costData.output_tokens || 0;

    console.log(`[COST_LOG] Parsed values - Input: ${inputCost}, Output: ${outputCost}, Total: ${totalCost}, InputTokens: ${inputTokens}, OutputTokens: ${outputTokens}`);
    console.log(`[COST_LOG] Call ID: ${callId || 'null'}`);

    const centerId = getCurrentCenterId();
    console.log(`[COST_LOG] Center ID: ${centerId || 'null'}`);

    await costLogService.logCost({
      agent: agentName,
      inputCost: inputCost,
      inputTokens: inputTokens,
      outputCost: outputCost,
      outputTokens: outputTokens,
      totalCost: totalCost,
      timestamp: new Date(costData.timestamp).toISOString(),
      call_id: callId || null,
      center_id: centerId
    });

    lastSavedCosts[agentName] = costSignature;

    console.log(`[COST_LOG] Successfully saved cost data to Supabase for agent: ${agentName}`);
  } catch (error) {
    console.error(`[COST_LOG] Failed to save cost data to Supabase for agent: ${agentName}`, error);
  }
};
